/**
 * Configurator state model + reducer.
 *
 * One typed reducer owns the whole configurator state. Components
 * read via `state.*` and write via `dispatch({type, ...})`. There's
 * no useState soup, no prop-drilling, no unmanaged async race
 * conditions — every async action carries its own request id so a
 * stale response from a previous discovery scan can't overwrite a
 * fresh one.
 *
 * The configurator has two top-level concerns:
 *   1. Scope assembly — what the buyer wants assessed.
 *   2. Identity + authorization — who's buying it and the typed-name
 *      attestation that authorizes the scan.
 *
 * Both concerns surface in the same page so the buyer can see how
 * scope decisions affect price as they type, and the order summary
 * recalculates on every state change without round-tripping back to
 * a wizard step.
 */

import type {
  AttestationCurrent,
  CartBreakdown,
  DiscoveredAsset,
} from "./api";

// --------------------------------------------------------------------------- //
// Discriminated unions for async states                                       //
// --------------------------------------------------------------------------- //
// Every async section of the configurator has the same four-state shape
// so components can render uniformly: idle → loading → ok | error.
// The `requestId` on loading/ok/error lets us discard stale responses
// when a new request supersedes an in-flight one.

export type AsyncState<T> =
  | { kind: "idle" }
  | { kind: "loading"; requestId: number }
  | { kind: "ok"; requestId: number; data: T }
  | { kind: "error"; requestId: number; message: string };

export const asyncIdle = <T,>(): AsyncState<T> => ({ kind: "idle" });

// --------------------------------------------------------------------------- //
// Configurator state shape                                                    //
// --------------------------------------------------------------------------- //

/** One additional asset beyond the primary domain.
 *
 * Domains get their own discovery + subdomain selection panel; IPs are
 * atomic (no subdomains). The `id` is a local UUID for React keying;
 * it never reaches the server.
 */
export interface AdditionalAsset {
  id: string;
  kind: "domain" | "ip";
  value: string;
  /** Only meaningful for kind=domain. */
  discovery: AsyncState<DiscoveredAsset[]>;
  /** Subdomains the buyer has explicitly checked. Hostnames, lowercase. */
  selectedSubs: string[];
}

export interface ContactInfo {
  name: string;
  email: string;
  phone: string;
  company: string;
}

export interface ReconConfiguratorState {
  // ---- Scope ----
  /** The primary domain the buyer types. Drives the first discovery scan. */
  primaryDomain: string;
  /** Discovery results for the primary domain. */
  primaryDiscovery: AsyncState<DiscoveredAsset[]>;
  /** Subdomains the buyer has checked under the primary domain. */
  primarySelectedSubs: string[];
  /** Additional domains or IPs beyond the primary. */
  additionalAssets: AdditionalAsset[];

  // ---- Contact ----
  contact: ContactInfo;

  // ---- Attestation ----
  /** Canonical attestation text + version + hash from the server. */
  attestation: AsyncState<AttestationCurrent>;
  /** Buyer's typed legal name for the attestation match. */
  attestedName: string;
  /** Whether the buyer has affirmatively checked the attestation box. */
  attestationChecked: boolean;

  // ---- Live pricing ----
  /** Latest server-authoritative price breakdown. Recomputed on every
   *  scope change; the SPA never invents its own pricing copy. */
  quote: AsyncState<CartBreakdown>;

  // ---- Submission ----
  /** Tracking state of the POST /api/v1/intake/checkout call. On `ok`
   *  the parent component immediately navigates to the Stripe URL the
   *  response contained — there's no "success" UI in the configurator
   *  itself, just a brief "Redirecting…" between dispatch and
   *  redirect. */
  submission:
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "redirecting"; checkoutUrl: string }
    | { kind: "error"; message: string };
}

export const initialReconConfiguratorState: ReconConfiguratorState = {
  primaryDomain: "",
  primaryDiscovery: asyncIdle(),
  primarySelectedSubs: [],
  additionalAssets: [],
  contact: { name: "", email: "", phone: "", company: "" },
  attestation: asyncIdle(),
  attestedName: "",
  attestationChecked: false,
  quote: asyncIdle(),
  submission: { kind: "idle" },
};

// --------------------------------------------------------------------------- //
// Actions (discriminated union)                                               //
// --------------------------------------------------------------------------- //

export type ReconConfiguratorAction =
  // Primary scope
  | { type: "set_primary_domain"; value: string }
  | { type: "primary_discovery_start"; requestId: number }
  | {
      type: "primary_discovery_ok";
      requestId: number;
      assets: DiscoveredAsset[];
    }
  | { type: "primary_discovery_error"; requestId: number; message: string }
  | { type: "toggle_primary_sub"; hostname: string }
  | { type: "select_all_primary_subs" }
  | { type: "clear_primary_subs" }
  // Additional assets
  | { type: "add_additional_domain"; id: string; value: string }
  | { type: "add_additional_ip"; id: string; value: string }
  | { type: "remove_additional_asset"; id: string }
  | { type: "set_additional_value"; id: string; value: string }
  | { type: "additional_discovery_start"; id: string; requestId: number }
  | {
      type: "additional_discovery_ok";
      id: string;
      requestId: number;
      assets: DiscoveredAsset[];
    }
  | {
      type: "additional_discovery_error";
      id: string;
      requestId: number;
      message: string;
    }
  | { type: "toggle_additional_sub"; id: string; hostname: string }
  // Contact
  | { type: "set_contact_field"; field: keyof ContactInfo; value: string }
  // Attestation
  | { type: "attestation_load_start"; requestId: number }
  | {
      type: "attestation_load_ok";
      requestId: number;
      data: AttestationCurrent;
    }
  | { type: "attestation_load_error"; requestId: number; message: string }
  | { type: "set_attested_name"; value: string }
  | { type: "set_attestation_checked"; checked: boolean }
  // Quote
  | { type: "quote_start"; requestId: number }
  | { type: "quote_ok"; requestId: number; breakdown: CartBreakdown }
  | { type: "quote_error"; requestId: number; message: string }
  // Submission
  | { type: "submission_start" }
  | { type: "submission_redirecting"; checkoutUrl: string }
  | { type: "submission_error"; message: string };

// --------------------------------------------------------------------------- //
// Reducer                                                                     //
// --------------------------------------------------------------------------- //

/** Pure reducer — no side effects, no async, no fetches. Components
 *  fire requests via the api.ts helpers and dispatch the resulting
 *  actions. The reducer's only job is to compute the next state. */
export function reconConfiguratorReducer(
  state: ReconConfiguratorState,
  action: ReconConfiguratorAction,
): ReconConfiguratorState {
  switch (action.type) {
    // ---- Primary scope ----
    case "set_primary_domain":
      return {
        ...state,
        primaryDomain: action.value,
        // Changing the primary domain invalidates the discovery + subs.
        // Don't carry stale data forward.
        primaryDiscovery: asyncIdle(),
        primarySelectedSubs: [],
      };
    case "primary_discovery_start":
      return {
        ...state,
        primaryDiscovery: {
          kind: "loading",
          requestId: action.requestId,
        },
      };
    case "primary_discovery_ok":
      // Reject stale responses — only the latest requestId wins.
      if (
        state.primaryDiscovery.kind !== "loading" ||
        state.primaryDiscovery.requestId !== action.requestId
      ) {
        return state;
      }
      return {
        ...state,
        primaryDiscovery: {
          kind: "ok",
          requestId: action.requestId,
          data: action.assets,
        },
        // Subdomains arrive UNSELECTED by default — the buyer
        // mindfully chooses what's in scope rather than discovering
        // we've already padded their cart. The 5-free affordance
        // is communicated visually in the grid (FREE / $15 badges)
        // and reinforced by the live quote.
        primarySelectedSubs: [],
      };
    case "primary_discovery_error":
      if (
        state.primaryDiscovery.kind !== "loading" ||
        state.primaryDiscovery.requestId !== action.requestId
      ) {
        return state;
      }
      return {
        ...state,
        primaryDiscovery: {
          kind: "error",
          requestId: action.requestId,
          message: action.message,
        },
      };
    case "toggle_primary_sub": {
      const lower = action.hostname.toLowerCase();
      const present = state.primarySelectedSubs.includes(lower);
      return {
        ...state,
        primarySelectedSubs: present
          ? state.primarySelectedSubs.filter((h) => h !== lower)
          : [...state.primarySelectedSubs, lower],
      };
    }
    case "select_all_primary_subs": {
      if (state.primaryDiscovery.kind !== "ok") return state;
      const apex = state.primaryDomain.toLowerCase();
      return {
        ...state,
        primarySelectedSubs: state.primaryDiscovery.data
          .map((a) => a.hostname.toLowerCase())
          .filter((h) => h !== apex),
      };
    }
    case "clear_primary_subs":
      return { ...state, primarySelectedSubs: [] };

    // ---- Additional assets ----
    case "add_additional_domain":
      return {
        ...state,
        additionalAssets: [
          ...state.additionalAssets,
          {
            id: action.id,
            kind: "domain",
            value: action.value,
            discovery: asyncIdle(),
            selectedSubs: [],
          },
        ],
      };
    case "add_additional_ip":
      return {
        ...state,
        additionalAssets: [
          ...state.additionalAssets,
          {
            id: action.id,
            kind: "ip",
            value: action.value,
            discovery: asyncIdle(),
            selectedSubs: [],
          },
        ],
      };
    case "remove_additional_asset":
      return {
        ...state,
        additionalAssets: state.additionalAssets.filter(
          (a) => a.id !== action.id,
        ),
      };
    case "set_additional_value":
      return {
        ...state,
        additionalAssets: state.additionalAssets.map((a) =>
          a.id === action.id
            ? {
                ...a,
                value: action.value,
                // Changing the value invalidates discovery + subs.
                discovery: asyncIdle(),
                selectedSubs: [],
              }
            : a,
        ),
      };
    case "additional_discovery_start":
      return {
        ...state,
        additionalAssets: state.additionalAssets.map((a) =>
          a.id === action.id
            ? {
                ...a,
                discovery: { kind: "loading", requestId: action.requestId },
              }
            : a,
        ),
      };
    case "additional_discovery_ok":
      return {
        ...state,
        additionalAssets: state.additionalAssets.map((a) => {
          if (a.id !== action.id) return a;
          if (
            a.discovery.kind !== "loading" ||
            a.discovery.requestId !== action.requestId
          ) {
            return a;
          }
          return {
            ...a,
            discovery: {
              kind: "ok",
              requestId: action.requestId,
              data: action.assets,
            },
            // Same mindful-selection default as the primary card.
            selectedSubs: [],
          };
        }),
      };
    case "additional_discovery_error":
      return {
        ...state,
        additionalAssets: state.additionalAssets.map((a) => {
          if (a.id !== action.id) return a;
          if (
            a.discovery.kind !== "loading" ||
            a.discovery.requestId !== action.requestId
          ) {
            return a;
          }
          return {
            ...a,
            discovery: {
              kind: "error",
              requestId: action.requestId,
              message: action.message,
            },
          };
        }),
      };
    case "toggle_additional_sub": {
      const lower = action.hostname.toLowerCase();
      return {
        ...state,
        additionalAssets: state.additionalAssets.map((a) => {
          if (a.id !== action.id) return a;
          const present = a.selectedSubs.includes(lower);
          return {
            ...a,
            selectedSubs: present
              ? a.selectedSubs.filter((h) => h !== lower)
              : [...a.selectedSubs, lower],
          };
        }),
      };
    }

    // ---- Contact ----
    case "set_contact_field":
      return {
        ...state,
        contact: { ...state.contact, [action.field]: action.value },
      };

    // ---- Attestation ----
    case "attestation_load_start":
      return {
        ...state,
        attestation: { kind: "loading", requestId: action.requestId },
      };
    case "attestation_load_ok":
      if (
        state.attestation.kind !== "loading" ||
        state.attestation.requestId !== action.requestId
      ) {
        return state;
      }
      return {
        ...state,
        attestation: {
          kind: "ok",
          requestId: action.requestId,
          data: action.data,
        },
      };
    case "attestation_load_error":
      if (
        state.attestation.kind !== "loading" ||
        state.attestation.requestId !== action.requestId
      ) {
        return state;
      }
      return {
        ...state,
        attestation: {
          kind: "error",
          requestId: action.requestId,
          message: action.message,
        },
      };
    case "set_attested_name":
      return { ...state, attestedName: action.value };
    case "set_attestation_checked":
      return { ...state, attestationChecked: action.checked };

    // ---- Quote ----
    case "quote_start":
      return {
        ...state,
        quote: { kind: "loading", requestId: action.requestId },
      };
    case "quote_ok":
      if (
        state.quote.kind !== "loading" ||
        state.quote.requestId !== action.requestId
      ) {
        return state;
      }
      return {
        ...state,
        quote: {
          kind: "ok",
          requestId: action.requestId,
          data: action.breakdown,
        },
      };
    case "quote_error":
      if (
        state.quote.kind !== "loading" ||
        state.quote.requestId !== action.requestId
      ) {
        return state;
      }
      return {
        ...state,
        quote: {
          kind: "error",
          requestId: action.requestId,
          message: action.message,
        },
      };

    // ---- Submission ----
    case "submission_start":
      return { ...state, submission: { kind: "loading" } };
    case "submission_redirecting":
      return {
        ...state,
        submission: { kind: "redirecting", checkoutUrl: action.checkoutUrl },
      };
    case "submission_error":
      return {
        ...state,
        submission: { kind: "error", message: action.message },
      };
  }
}

// --------------------------------------------------------------------------- //
// Derived selectors                                                           //
// --------------------------------------------------------------------------- //
// Pure functions over state for things the UI needs but the reducer
// doesn't need to track explicitly. Co-located with the reducer so
// the state shape and the queries against it stay in sync.

/** Build the cart-shape array the server expects from the current
 *  state. Used to drive both the live quote endpoint and the final
 *  checkout submission — both endpoints reclassify domain / subdomain
 *  / IP server-side, so the SPA's `kind` claim here is only a hint.
 */
export function buildCartFromState(state: ReconConfiguratorState): Array<{
  value: string;
  kind: "domain" | "subdomain" | "ip";
}> {
  const cart: Array<{ value: string; kind: "domain" | "subdomain" | "ip" }> = [];

  const primary = state.primaryDomain.trim().toLowerCase();
  if (primary) {
    cart.push({ value: primary, kind: "domain" });
    for (const sub of state.primarySelectedSubs) {
      const lower = sub.trim().toLowerCase();
      if (lower && lower !== primary) {
        cart.push({ value: lower, kind: "subdomain" });
      }
    }
  }

  for (const asset of state.additionalAssets) {
    const value = asset.value.trim().toLowerCase();
    if (!value) continue;
    cart.push({ value, kind: asset.kind === "ip" ? "ip" : "domain" });
    if (asset.kind === "domain") {
      for (const sub of asset.selectedSubs) {
        const lower = sub.trim().toLowerCase();
        if (lower && lower !== value) {
          cart.push({ value: lower, kind: "subdomain" });
        }
      }
    }
  }

  return cart;
}

/** Returns true when the form has enough state to attempt a quote
 *  request — at minimum, a non-empty primary domain or at least one
 *  additional asset. */
export function canQuote(state: ReconConfiguratorState): boolean {
  if (state.primaryDomain.trim().length > 0) return true;
  for (const a of state.additionalAssets) {
    if (a.value.trim().length > 0) return true;
  }
  return false;
}

/** Returns true when the form is fully ready to submit — cart valid,
 *  contact complete, attestation typed and checked. Used by the Pay
 *  button to enable/disable. */
export function canSubmit(state: ReconConfiguratorState): boolean {
  if (!canQuote(state)) return false;
  if (state.quote.kind !== "ok") return false;
  if (!state.contact.name.trim()) return false;
  if (!state.contact.email.trim()) return false;
  if (state.attestation.kind !== "ok") return false;
  if (!state.attestationChecked) return false;
  if (!state.attestedName.trim()) return false;
  return true;
}
